import React from "react";
import { useTranslation } from "utilities/i18n";
import { generatePath, useParams, useRouteMatch } from "react-router";
import { useHistory } from "polyrouter";
import { protect } from "TsRoleGuards";
import { DeleteModal, useModalDispatch } from "collections/Modal";
import { DISMISS, useNotification } from "collections";
import { constructFilterList } from "utilities/filters/deviceFilters";
import {
  Maybe,
  useDeleteDevicesMutation,
  useDeviceDetailsQuery,
  useUnregisterDeviceMutation
} from "generated/graphql";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

export const DeleteDeviceModal: React.FC<{
  deviceId: string;
  deviceName: string;
  connectedDevice: boolean;
}> = ({ deviceId, deviceName, connectedDevice }) => {
  const [{ fetching }, deleteDevice] = useUnregisterDeviceMutation();
  const [addNotification] = useNotification();
  const { push } = useHistory();
  const tenantId = useTenantId();
  const { t } = useTranslation();
  const { url } = useRouteMatch();

  return (
    <DeleteModal
      disabled={fetching}
      onDelete={async () => {
        const name = deviceName;
        const { data } = await deleteDevice(
          { deviceId },
          { notify: { message: t("DELETE_DEVICE_FAILURE", { name }) } }
        );

        const success = data?.unregisterDevice.success;
        if (success) {
          push(
            connectedDevice
              ? generatePath(
                  url.replace(`/connected-device/${deviceId}/delete`, "")
                )
              : `/manage/inventory/table?tenantId=${tenantId}`
          );
          addNotification({
            type: "success",
            message: t("DELETE_DEVICE_SUCCESS", { name }),
            dismiss: DISMISS.DEFAULT
          });
        }

        if (!success) {
          addNotification({
            type: "error",
            message: t("DELETE_DEVICE_FAILURE", { name }),
            dismiss: "USER_ONLY"
          });
        }
      }}
      entity={deviceName}
      confirmationString={t("DELETE_DEVICE_CONFIRMATION", {
        deviceName,
        confirm: "CANNOT_BE_UNDONE"
      })}
      name="DeleteDevice"
    />
  );
};

export interface DeviceInfo {
  id: string;
  serial: Maybe<string>;
  name: Maybe<string>;
  hardwareModel: Maybe<string>;
  lastConfigRequestDate: Maybe<string>;
}

export const DeleteBulkDevicesModal: React.FC<{
  devices: DeviceInfo[];
  refresh: () => void;
}> = ({ devices, refresh }) => {
  const [, deleteDevice] = useUnregisterDeviceMutation();
  const [, deleteDevices] = useDeleteDevicesMutation();
  const tenantId = useTenantId();

  const [addNotification] = useNotification();
  const closeModal = useModalDispatch();
  const { t } = useTranslation();

  const displayDevices = () => {
    return (
      <div className="small-text overflow-list modal-body pt-1 pl-1">
        {devices.map(device => (
          <div key={device.id} className="mb-1">
            <p className="list-item">{`${device.name} - ${device.id}`}</p>
          </div>
        ))}
      </div>
    );
  };

  const handleDelete = async () => {
    let deletionError = false;

    let serials: string[] = [];
    for (const { id, name, serial } of devices) {
      if (serial) serials.push(serial);
      const { error } = await deleteDevice({ deviceId: id });
      if (error) {
        deletionError = true;
        addNotification({
          type: "error",
          message: t("DELETE_DEVICE_FAILURE", { name }),
          dismiss: DISMISS.USER_ONLY,
          persistThroughNav: true
        });
      }
    }
    await deleteDevices({ tenantId, serials });

    if (!deletionError) {
      addNotification({
        type: "success",
        message: t("DELETE_DEVICE_SUCCESS", {
          name: devices[0].name,
          count: devices.length
        }),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
    }
    closeModal();
    refresh();
  };

  return (
    <DeleteModal
      entity={t("DEVICES")}
      confirmationString={t("DELETE_BULK_DEVICES_CONFIRMATION")}
      name="DeleteDevices"
      onDelete={handleDelete}
    >
      {displayDevices()}
    </DeleteModal>
  );
};

const DeleteDeviceModalLoader: React.FC<{ id: string }> = ({ id }) => {
  const { connectedDeviceId } = useParams<{
    connectedDeviceId?: string;
  }>();
  const { data } = useDeviceDetailsQuery({
    variables: {
      filter: {
        AND: constructFilterList({
          id: connectedDeviceId ?? id
        })
      }
    }
  });

  const device = data?.devices[0];

  return (
    <DeleteDeviceModal
      connectedDevice={!!connectedDeviceId}
      deviceId={connectedDeviceId ?? id}
      deviceName={device?.name || device?.macAddress || id}
    />
  );
};

export default protect("unregister:device")(DeleteDeviceModalLoader);
